import {
  FreeLayoutPluginContext, FlowNodeEntity, WorkflowDragService,
  WorkflowNodeLinesData,
  UndoRedoChangeElementEvent,
  WorkflowSelectService,
  FreeOperationType,
  Disposable,
  FlowNodeBaseType,
} from '@flowgram.ai/free-layout-editor';
import { WorkflowOperationBaseService } from '@flowgram.ai/free-layout-core';
import { nanoid } from 'nanoid';
import { debounce } from 'lodash';
import { FlowNodeTypeEnum } from '$/domains/node/type';
import { flowMtstatTrack } from '$/store/useFlowStore';
import { editNodeFlowType } from '$/pages/RoboneoFlow/NodeAddPanel/components/NodeAdd/constant';
import { lineReBuild, lineReBuildWithChildren, lineReBuildWithChildrenForNew } from './lineReBuild';
import { getMediaContentDescription } from './nodeDescriptionAdd';
import { scheduleRegisterWorkflowNodeMediaAsset } from '$/utils/workflowMediaAssetRegister';

// const FOLD_HEIGHT = 40;
const OFFSET = 6;
const styleId = 'high-level-select';

export const getSuffix = (url: string) => {
  return url.split('.').pop() || '';
};

/**
 * 创建一个时间回调函数，接收时间参数并执行节点添加预处理
 * @param params 节点添加所需的参数
 * @returns 返回一个接收时间参数的回调函数
 */
export const generateTimeCallback = (params: {
  ctx: FreeLayoutPluginContext;
  currentNode: FlowNodeEntity;
  // data: any;
  generateNodeType: FlowNodeTypeEnum;
}) => {
  return (time: number): void => {
    let _time = time;
    // if (!time) return;
    if (!_time) _time = 10000;
    // 直接执行，不使用 setTimeout
    withoutHistory(params.ctx, () => {
      generateNodeAddPre({ ...params, estimatedTime: _time });
    });
  };
};

/**
 * 包装函数，使其不进历史记录
 * 使用 history.stop() 和 history.start() 包装函数执行
 * @param ctx flowgram 上下文
 * @param fn 需要包装的函数
 * @returns 函数执行结果
 */
export const withoutHistory = <T>(
  ctx: FreeLayoutPluginContext,
  fn: () => T,
): T => {
  const { history } = ctx;
  history.stop();
  try {
    return fn();
  } finally {
    history.start();
  }
};

/**
 * 包装异步函数，使其不进历史记录
 * @param ctx flowgram 上下文
 * @param fn 需要包装的异步函数
 * @returns Promise 执行结果
 */
export const withoutHistoryAsync = async <T>(
  ctx: FreeLayoutPluginContext,
  fn: () => Promise<T>,
): Promise<T> => {
  const { history } = ctx;
  history.stop();
  try {
    return await fn();
  } finally {
    history.start();
  }
};

/**
 * 预加载节点
 */
export const generateNodeAddPre = ({
  ctx,
  currentNode,
  // updateData,
  // data,
  generateNodeType,
  estimatedTime,
}: {
  ctx: FreeLayoutPluginContext;
  currentNode: FlowNodeEntity;
  // updateData: (data: any) => void;
  // data: any;
  generateNodeType: FlowNodeTypeEnum;
  estimatedTime: number;
}) => {
  // console.log('generateNodeAddPre', ctx.history);
  // ctx.history.stop();
  // 在这里实现节点添加的预处理逻辑
  const data = currentNode.toJSON()?.data;
  console.log('预估时间:', estimatedTime);
  const updateData = currentNode.form!.updateFormValues;
  const preNodeId = nanoid();
  const childrenNodeList = data?.childrenNodeList || [];
  if (childrenNodeList.length) {
    layoutOldNodes(ctx, childrenNodeList);
  }
  const lastY = currentNode.transform.position.y + (currentNode.transform.bounds.height + OFFSET);
  const nodeData = generateNodeData(preNodeId, currentNode, generateNodeType);
  const isPre = true;
  const _node = ctx.document.createWorkflowNode({
    id: preNodeId,
    type: generateNodeType,
    meta: {
      position: {
        x: currentNode.transform.position.x,
        y: lastY,
      },
    },
    data: { ...nodeData, isPre },
  });
  const operationService = ctx.get(WorkflowOperationBaseService) as WorkflowOperationBaseService;
  if (currentNode.parent && currentNode.parent.flowNodeType !== FlowNodeBaseType.ROOT) {
    operationService.moveNode(_node, { parent: currentNode.parent });
    operationService.updateNodePosition(_node, {
      x: currentNode.transform.position.x,
      y: lastY,
    });
  }
  const debouncedHandler = debounce((_ctx, _currentNode) => {
    layoutNodes(_ctx, _currentNode);
  }, 50);
  generateExtInfo(_node, { isPre, estimatedTime });
  _node.onEntityChange(() => {
    debouncedHandler(ctx, currentNode);
  });
  _node.onDispose(() => {
    const { childrenNodeList: _childrenNodeList } = currentNode.toJSON()?.data || { childrenNodeList: [] };
    const filteredList = _childrenNodeList?.filter((item: string) => item !== _node.id);
    const _data = currentNode.toJSON()?.data;
    updateData({
      ..._data,
      childrenNodeList: filteredList,
    });
    layoutNodes(ctx, currentNode);
  });

  updateData({
    ...data,
    childrenNodeList: [preNodeId, ...data?.childrenNodeList || []],
  });

  // generateNodeAdd({
  //   ctx,
  //   currentNode,
  //   updateData,
  //   data,
  //   generateNodeType,
  // });
  // 如果有参数，可以继续执行 generateNodeAdd
  // if (params && params.ctx && params.currentNode) {
  //   const { estimatedTime, ...nodeAddParams } = params;
  //   generateNodeAdd(nodeAddParams as Required<Omit<typeof params, 'estimatedTime'>>);
  // }
};
const WORKFLOW_MEDIA_CREATE_NODE_TYPES: FlowNodeTypeEnum[] = [
  FlowNodeTypeEnum.IMAGE_NODE,
  FlowNodeTypeEnum.VIDEO_NODE,
  FlowNodeTypeEnum.AUDIO_NODE,
  FlowNodeTypeEnum.GIF_NODE,
];

export const generateNodeAdd = async ({
  ctx, currentNode, nums = 3, generateNodeType, urlList, taskId, amt, apiName, originList = [], assetIds, passThroughTaskId,
}: {
  ctx: FreeLayoutPluginContext;
  currentNode: FlowNodeEntity;
  nums?: number;
  // data: any;
  generateNodeType: FlowNodeTypeEnum;
  urlList: string[];
  taskId?: string;
  amt?: number;
  apiName?: string;
  originList?: string[];
  /** 与 `urlList` 同序：来自 nodeexecutequery output 中解析的 asset_id（可选） */
  assetIds?: (number | undefined)[];
  passThroughTaskId?: string;
}) => {
  // currentNode 为父节点
  const idList = Array.from({ length: nums }, () => nanoid());
  const data = currentNode.toJSON()?.data;
  const childrenNodeList = data?.childrenNodeList || [];
  const disList: Array<{ dispose: () => void; }> = [];
  const updateData = currentNode.form!.updateFormValues;

  const nodeLines = currentNode.getData(WorkflowNodeLinesData);
  const needReBuildLine = !!nodeLines.outputLines.length;

  if (childrenNodeList.length) {
    layoutOldNodes(ctx, childrenNodeList);
  }

  const debouncedHandler = debounce((_ctx, _currentNode) => {
    layoutNodes(_ctx, _currentNode);
  }, 50);

  idList.forEach((id, index) => {
    const lastY = currentNode.transform.position.y + (currentNode.transform.bounds.height + OFFSET) * (index + 1);

    const nodeData = generateNodeData(
      id,
      currentNode,
      generateNodeType,
      urlList[index],
      taskId,
      originList[index],
      assetIds?.[index],
    );

    const _node = ctx.document.createWorkflowNode({
      id,
      type: generateNodeType,
      meta: {
        position: {
          x: currentNode.transform.position.x,
          y: lastY,
        },
      },
      data: nodeData,
    });
    const operationService = ctx.get(WorkflowOperationBaseService) as WorkflowOperationBaseService;
    if (currentNode.parent && currentNode.parent.flowNodeType !== FlowNodeBaseType.ROOT) {
      operationService.moveNode(_node, { parent: currentNode.parent });
      operationService.updateNodePosition(_node, {
        x: currentNode.transform.position.x,
        y: lastY,
      });
    }

    generateExtInfo(_node);

    if (WORKFLOW_MEDIA_CREATE_NODE_TYPES.includes(generateNodeType)) {
      scheduleRegisterWorkflowNodeMediaAsset(_node, { sourceTaskId: taskId, ctx });
    }

    // 所有节点都使用同一个 debounced 函数
    const dis = _node.onEntityChange(() => {
      debouncedHandler(ctx, currentNode);
    });
    _node.onDispose(() => {
      const { childrenNodeList: _childrenNodeList } = currentNode.toJSON()?.data || { childrenNodeList: [] };
      const filteredList = _childrenNodeList?.filter((item: string) => item !== _node.id);
      const _data = currentNode.toJSON()?.data;
      updateData({
        ..._data,
        childrenNodeList: filteredList,
      });
      layoutNodes(ctx, currentNode);
    });
    disList.push(dis);

    flowMtstatTrack('workflow_output_create', {
      node_id: id,
      content_type: generateNodeType,
      model_name: apiName,
      amt,
      source_task_id: taskId,
      source: passThroughTaskId ? 'agent_chat' : '',
    });
    flowMtstatTrack('workflow_input_create', {
      node_id: id,
      content_type: generateNodeType,
      model_name: apiName,
      source_task_id: taskId,
      source: passThroughTaskId ? 'agent_chat' : 'generate',
    });
    generateNodeDesc(generateNodeType, _node, nodeData, passThroughTaskId);
  });

  updateData({
    ...data,
    childrenNodeList: [...idList, ...data?.childrenNodeList || []],
  });

  if (needReBuildLine) {
    const firstChild = ctx.document.getNode(idList[0]);
    await lineReBuild(ctx, currentNode, firstChild);
  } else if (idList.length === 1) {
    const firstChild = ctx.document.getNode(idList[0]);
    await lineReBuildWithChildrenForNew(ctx, currentNode, firstChild);
    // await lineReBuildWithChildren(ctx, currentNode);
  }

  if (idList.length) {
    const lastId = idList[idList.length - 1];
    const lastNode = ctx.document.getNode(lastId)!;
    ctx.document.selectServices.selectNode(lastNode);
  }

  return idList;
};

const generateExtInfo = (node: FlowNodeEntity, params: { isPre?: boolean; estimatedTime?: number; } = {}) => {
  node.updateExtInfo({
    inDragging: false,
    ...params,
  });
};

const generateNodeData = (
  id: string,
  currentNode: FlowNodeEntity,
  generateNodeType: FlowNodeTypeEnum,
  url?: string,
  taskId?: string,
  originUrl?: string,
  assetIdFromExecute?: number,
) => {
  // 兜底：如果 originUrl 没有值，使用 url 作为兜底
  const _originUrl = originUrl || url;
  const assetIdFields = assetIdFromExecute != null && Number.isFinite(assetIdFromExecute) && assetIdFromExecute > 0
    ? { assetId: Math.trunc(assetIdFromExecute) }
    : {};
  if (generateNodeType === FlowNodeTypeEnum.TEXT_NODE) {
    return {
      textList: [{
        value: url,
      }],
      draggable: false,
      parentNodeId: currentNode.id,
      isGenerated: true,
      taskId,
    };
  }
  if (generateNodeType === FlowNodeTypeEnum.IMAGE_NODE) {
    return {
      ...assetIdFields,
      imageList: [{
        url,
        suffix: getSuffix(url || ''),
        name: url,
        type: 'image',
        originUrl: _originUrl,
      }],
      draggable: false,
      parentNodeId: currentNode.id,
      isGenerated: true,
      taskId,
    };
  }
  if (generateNodeType === FlowNodeTypeEnum.VIDEO_NODE) {
    return {
      ...assetIdFields,
      videoList: [{
        url,
        suffix: getSuffix(url || ''),
        name: url,
        type: 'video',
        originUrl: _originUrl,
      }],
      draggable: false,
      parentNodeId: currentNode.id,
      isGenerated: true,
      taskId,
    };
  }
  if (generateNodeType === FlowNodeTypeEnum.AUDIO_NODE) {
    return {
      ...assetIdFields,
      audioList: [{
        url,
        suffix: getSuffix(url || ''),
        name: Date.now().toString(),
        type: 'audio',
        originUrl: _originUrl,
      }],
      draggable: false,
      parentNodeId: currentNode.id,
      isGenerated: true,
      taskId,
    };
  }
  if (generateNodeType === FlowNodeTypeEnum.GIF_NODE) {
    return {
      ...assetIdFields,
      imageList: [{
        url,
        suffix: getSuffix(url || ''),
        name: url,
        type: 'gif',
        originUrl: _originUrl,
      }],
      draggable: false,
      parentNodeId: currentNode.id,
      isGenerated: true,
      taskId,
    };
  }
  return {
    draggable: false,
    parentNodeId: currentNode.id,
    isGenerated: true,
  };
};

// export const normalizeNodeDataWithType = (generateNodeType: FlowNodeTypeEnum, url?: string, meta?: Record<string, any>) => {

// }

export const normalizeNodeData = (generateNodeType: FlowNodeTypeEnum, url?: string, meta?: Record<string, any>) => {
  const assetIdRaw = meta?.assetId;
  const assetIdFields = assetIdRaw != null && Number.isFinite(Number(assetIdRaw)) && Number(assetIdRaw) > 0
    ? { assetId: Math.trunc(Number(assetIdRaw)) }
    : {};
  if (generateNodeType === FlowNodeTypeEnum.TEXT_NODE) {
    return {
      type: generateNodeType,
      textList: [{
        value: url,
      }],
    };
  }
  if (generateNodeType === FlowNodeTypeEnum.IMAGE_NODE) {
    return {
      type: generateNodeType,
      ...assetIdFields,
      imageList: [{
        url,
        suffix: getSuffix(url || ''),
        name: url,
        type: 'image',
        originUrl: url,
      }],
    };
  }
  if (generateNodeType === FlowNodeTypeEnum.VIDEO_NODE) {
    return {
      type: generateNodeType,
      ...assetIdFields,
      videoList: [{
        url,
        suffix: getSuffix(url || ''),
        name: url,
        type: 'video',
        originUrl: url,
      }],
    };
  }
  if (generateNodeType === FlowNodeTypeEnum.AUDIO_NODE) {
    const displayName = (meta?.name && String(meta.name).trim()) || `${Date.now()}`;
    return {
      type: generateNodeType,
      ...assetIdFields,
      audioList: [{
        url,
        suffix: getSuffix(url || ''),
        name: displayName,
        type: 'audio',
        originUrl: url,
      }],
    };
  }
  if (generateNodeType === FlowNodeTypeEnum.GIF_NODE) {
    return {
      type: generateNodeType,
      ...assetIdFields,
      imageList: [{
        url,
        suffix: getSuffix(url || ''),
        name: url,
        type: 'gif',
        originUrl: url,
      }],
    };
  }
  return {};
};

export const layoutNodes = (ctx: FreeLayoutPluginContext, currentNode: FlowNodeEntity) => {
  // currentNode 为父节点
  const { data } = currentNode.toJSON();
  const childrenNodeList = data?.childrenNodeList || [];
  const { bottom } = currentNode.transform.bounds;
  // 处理群组：获取父容器的世界坐标 Y 偏移
  let parentOffsetY = 0;
  const parentNode = currentNode.parent;
  if (parentNode && parentNode.flowNodeType !== FlowNodeBaseType.ROOT) {
    const worldTransform = parentNode.transform?.transform?.worldTransform;
    if (worldTransform) {
      parentOffsetY = worldTransform.ty;
    }
  }

  childrenNodeList.forEach((id: string, index: number) => {
    const node = ctx.document.getNode(id);
    if (!node) return;

    const { width } = currentNode.transform.bounds;

    const positionTL = currentNode.transform.position.x - width / 2;

    const lastBottom = index
      ? (ctx.document.getNode(childrenNodeList[index - 1])?.transform.bounds.bottom || bottom)
      : bottom;

    // 使用新方法检查位置关系
    // const shouldCollapse = isNodeBelowAndWithinBounds(currentNode, node);

    const position = {
      x: positionTL + node.transform.bounds.width / 2,
      // x: currentNode.transform.position.x,
      y: lastBottom + OFFSET - parentOffsetY,
    };

    // 直接设置 transform.position
    node.transform.transform.update({
      position,
    });
  });
};

export const layoutRelateNodes = (ctx: FreeLayoutPluginContext, currentNode: FlowNodeEntity, offsetParentPosition: { x: number; y: number; }, siblingNodeOffsetList: Array<{ x: number; y: number; }>) => {
  // 各个子节点和当前节点的Y轴位置
  const { data } = currentNode.toJSON();
  const parentNodeId = data?.parentNodeId;
  const parentNode = ctx.document.getNode(parentNodeId);
  if (!parentNode) return;

  const offsetParentY = offsetParentPosition.y;
  const positionTL = currentNode.transform.position.x - currentNode.transform.bounds.width / 2;
  parentNode.transform.transform.update({
    position: {
      x: positionTL + parentNode.transform.bounds.width / 2,
      y: currentNode.transform.position.y - offsetParentY,
    },
  });
  const childrenNodeList = parentNode?.toJSON()?.data?.childrenNodeList || [];
  childrenNodeList.forEach((id: string, index: number) => {
    const node = ctx.document.getNode(id);
    if (!node) return;
    const offsetY = siblingNodeOffsetList[index].y;

    // 使用新方法检查位置关系
    // const shouldCollapse = isNodeBelowAndWithinBounds(currentNode, node);

    const position = {
      x: positionTL + node.transform.bounds.width / 2,
      y: currentNode.transform.position.y - offsetY,
    };

    // 直接设置 transform.position
    node.transform.transform.update({
      position,
    });
  });
};

/**
 * 检查子节点是否在当前节点的下方且在左右范围内
 * @param currentNode 当前节点
 * @param node 子节点
 * @returns boolean 如果子节点在当前节点下方且在左右范围内返回true
 */
export const isNodeBelowAndWithinBounds = (currentNode: FlowNodeEntity, node: FlowNodeEntity): boolean => {
  const { left: currentLeft, right: currentRight, bottom: currentBottom } = currentNode.transform.bounds;
  const {
    left: nodeLeft, right: nodeRight, top: nodeTop,
  } = node.transform.bounds;

  // 检查子节点是否在当前节点下方
  const isBelowCurrentNode = nodeTop > currentBottom;

  // 检查子节点是否在当前节点的左右范围内（有重叠）
  const isWithinHorizontalBounds = nodeLeft < currentRight && nodeRight > currentLeft;

  return isBelowCurrentNode && isWithinHorizontalBounds;
};

/**
 * 布局旧节点
 * 需要折叠
 */
export const layoutOldNodes = (ctx: FreeLayoutPluginContext, oldChildrenNodeList: string[]) => {
  oldChildrenNodeList.forEach((id) => {
    const node = ctx.document.getNode(id);
    if (!node) return;

    // 使用新方法检查位置关系
    // const shouldCollapse = isNodeBelowAndWithinBounds(currentNode, node);
    const updateData = node.form!.updateFormValues;
    updateData({
      ...node.toJSON()?.data,
      expanded: true, // 如果在范围内则折叠，否则展开
    });
    node.renderData.update({
      ...node.renderData,
      expanded: true, // 如果在范围内则折叠，否则展开
    });
  });
};

const layoutPushHistory = (ctx: FreeLayoutPluginContext, node: FlowNodeEntity, childrenOldPosList: Array<{ x: number; y: number; }>, childrenNewPosList: Array<{ x: number; y: number; }>) => {
  if (childrenOldPosList.length === 0 || childrenNewPosList.length === 0) return;
  if (childrenOldPosList.length !== childrenNewPosList.length) return;
  const { history } = ctx;
  const childrenNodeList = node.toJSON()?.data?.childrenNodeList || [];
  // history.transact(() => {
  childrenNodeList.forEach((id: string, index: number) => {
    const _node = ctx.document.getNode(id);
    if (!_node) return;
    history.pushOperation(
      {
        type: FreeOperationType.dragNodes,
        value: {
          ids: [_node.id],
          value: [
            {
              x: childrenNewPosList[index].x,
              y: childrenNewPosList[index].y,
            },
          ],
          oldValue: [
            {
              x: childrenOldPosList[index].x,
              y: childrenOldPosList[index].y,
            },
          ],
        },
      },
      {
        noApply: true,
      },
    );
  });
  // });
};

const layoutRelatePushHistory = (ctx: FreeLayoutPluginContext, childNode: FlowNodeEntity, childrenOldPosList: Array<{ x: number; y: number; }>, childrenNewPosList: Array<{ x: number; y: number; }>, offsetParentPosition: { x: number; y: number; }) => {
  if (childrenNewPosList.length === 0 || childrenOldPosList.length === 0) return;
  if (childrenNewPosList.length !== childrenOldPosList.length) return;
  if (!childNode || !offsetParentPosition) return;
  const { history } = ctx;
  const parentNode = ctx.document.getNode(childNode.toJSON()?.data?.parentNodeId);
  const index = parentNode?.toJSON()?.data?.childrenNodeList.findIndex((item: string) => item === childNode.id);
  const oldPos = childrenOldPosList[index];
  const newPos = childrenNewPosList[index];
  const oldParentPos = {
    x: oldPos.x - offsetParentPosition.x,
    y: oldPos.y - offsetParentPosition.y,
  };
  const newParentPos = {
    x: newPos.x - offsetParentPosition.x,
    y: newPos.y - offsetParentPosition.y,
  };
  if (!parentNode) return;
  history.pushOperation(
    {
      type: FreeOperationType.dragNodes,
      value: {
        ids: [parentNode.id],
        value: [
          {
            x: newParentPos.x,
            y: newParentPos.y,
          },
        ],
        oldValue: [
          {
            x: oldParentPos.x,
            y: oldParentPos.y,
          },
        ],
      },
    },
    {
      noApply: true,
    },
  );

  childrenNewPosList.forEach((item, _index) => {
    const _node = ctx.document.getNode(parentNode.toJSON()?.data?.childrenNodeList[index]);
    if (!_node) return;
    history.pushOperation(
      {
        type: FreeOperationType.dragNodes,
        value: {
          ids: [_node.id],
          value: [
            {
              x: item.x,
              y: item.y,
            },
          ],
          oldValue: [
            {
              x: childrenOldPosList[index].x,
              y: childrenOldPosList[index].y,
            },
          ],
        },
      },
      {
        noApply: true,
      },
    );
  });
};

export const nodeDrag = (ctx: FreeLayoutPluginContext, dragService: WorkflowDragService) => {
  /**
   * 分别维护父节点和子节点的拖拽效果
   * 父节点拖拽时，子节点需要跟随父节点移动
   * 子节点拖拽时，兄弟节点和父节点需要跟随子节点移动
   * offsetParentPosition 用于记录节点与父节点的偏移量
   * siblingNodeOffsetMap 用于记录兄弟节点与被拖拽的子节点的偏移量
   * relateInDraggingList 用于记录被拖拽的节点的id(父节点id，如果节点为父节点，记录本身id)
   */
  const offsetParentPositionMap = new Map<string, { x: number; y: number; }>();
  const relateInDraggingList: string[] = [];
  // const siblingNodeOffsetList: Array<{ x: number; y: number; }> = [];
  const siblingNodeOffsetMap = new Map<string, Array<{ x: number; y: number; }>>();
  const childrenOldPosMap = new Map<string, Array<{ x: number; y: number; }>>();
  const childrenNewPosMap = new Map<string, Array<{ x: number; y: number; }>>();
  const { history } = ctx;
  dragService.onNodesDrag((e) => {
    const { nodes, type } = e;
    nodes.forEach((node: FlowNodeEntity) => {
      const nodeData = node.toJSON()?.data || {};

      if (editNodeFlowType.includes(node.flowNodeType as FlowNodeTypeEnum)) {
        if (type === 'onDragStart') {
          relateInDraggingList.push(node?.id);
          const { childrenNodeList } = node.toJSON()?.data || { childrenNodeList: [] };
          const childrenOldPosList: Array<{ x: number; y: number; }> = [];
          if (!childrenNodeList?.length) return;
          childrenNodeList.forEach((id: string) => {
            const _node = ctx.document.getNode(id);
            if (!_node) return;
            childrenOldPosList.push({
              x: _node.transform.position.x,
              y: _node.transform.position.y,
            });
          });
          childrenOldPosMap.set(node.id, childrenOldPosList);
          history.startTransaction();
        }
        if (type === 'onDragging') {
          const { childrenNodeList } = node.toJSON()?.data || { childrenNodeList: [] };
          if (childrenNodeList?.length) {
            layoutNodes(ctx, node);
          }
        }
        if (type === 'onDragEnd') {
          relateInDraggingList.splice(relateInDraggingList.indexOf(node?.id), 1);
          const { childrenNodeList } = node.toJSON()?.data || { childrenNodeList: [] };
          const childrenNewPosList: Array<{ x: number; y: number; }> = [];
          if (!childrenNodeList?.length) {
            // 拖动结束后恢复焦点,防止节点无法被删除
            ctx.playground.node.focus();
            return;
          }
          childrenNodeList.forEach((id: string) => {
            const _node = ctx.document.getNode(id);
            if (!_node) return;
            childrenNewPosList.push({
              x: _node.transform.position.x,
              y: _node.transform.position.y,
            });
          });
          childrenNewPosMap.set(node.id, childrenNewPosList);
          try {
            layoutPushHistory(ctx, node, childrenOldPosMap.get(node.id)!, childrenNewPosList);
          } catch (_e) {
            console.warn(_e);
          }

          history.endTransaction();
          // 拖动结束后恢复焦点,防止节点无法被删除
          ctx.playground.node.focus();
        }
      }

      if (nodeData?.isGenerated) {
        if (type === 'onDragStart') {
          if (relateInDraggingList.includes(nodeData?.parentNodeId)) return;
          const parentNode = ctx.document.getNode(nodeData?.parentNodeId);
          if (!parentNode) return;
          siblingNodeOffsetMap.set(parentNode.id, []);
          const siblingNodeList = parentNode?.toJSON()?.data?.childrenNodeList || [];
          siblingNodeList.forEach((id: string) => {
            const siblingNode = ctx.document.getNode(id);
            if (!siblingNode) return;
            siblingNodeOffsetMap.get(parentNode.id)?.push({
              x: node.transform.position.x - siblingNode.transform.position.x,
              y: node.transform.position.y - siblingNode.transform.position.y,
            });
          });
          if (!parentNode) return;
          offsetParentPositionMap.set(parentNode.id, {
            x: node.transform.position.x - parentNode.transform.position.x,
            y: node.transform.position.y - parentNode.transform.position.y,
          });
          if (!relateInDraggingList.includes(parentNode?.id)) {
            node.updateExtInfo({
              inDragging: true,
            });
            relateInDraggingList.push(parentNode?.id);
          }
          const childrenOldPosList: { x: number; y: number; }[] = [];
          siblingNodeList.forEach((id: string) => {
            const siblingNode = ctx.document.getNode(id);
            if (!siblingNode) return;
            childrenOldPosList.push({
              x: siblingNode.transform.position.x,
              y: siblingNode.transform.position.y,
            });
          });
          childrenOldPosMap.set(parentNode.id, childrenOldPosList);
          history.startTransaction();
        }
        if (type === 'onDragging') {
          if (!node.getExtInfo()?.inDragging) return;
          layoutRelateNodes(ctx, node, offsetParentPositionMap.get(nodeData?.parentNodeId)!, siblingNodeOffsetMap.get(nodeData?.parentNodeId)!);
        }
        if (type === 'onDragEnd') {
          const parentNodeId = nodeData?.parentNodeId;
          node.updateExtInfo({
            inDragging: false,
          });
          const siblingNodeList = ctx.document.getNode(parentNodeId)?.toJSON()?.data?.childrenNodeList || [];
          const childrenNewPosList: { x: number; y: number; }[] = [];
          siblingNodeList.forEach((id: string) => {
            const siblingNode = ctx.document.getNode(id);
            if (!siblingNode) return;
            childrenNewPosList.push({
              x: siblingNode.transform.position.x,
              y: siblingNode.transform.position.y,
            });
          });
          childrenNewPosMap.set(parentNodeId, childrenNewPosList);
          try {
            layoutRelatePushHistory(ctx, node, childrenOldPosMap.get(parentNodeId)!, childrenNewPosList, offsetParentPositionMap.get(parentNodeId)!);
          } catch (_e) {
            console.warn(_e);
          }
          relateInDraggingList.splice(relateInDraggingList.indexOf(parentNodeId), 1);
          // siblingNodeOffsetList.length = 0;
          offsetParentPositionMap.delete(parentNodeId);
          siblingNodeOffsetMap.delete(parentNodeId);
          history.endTransaction();
          // 拖动结束后恢复焦点,防止节点无法被删除
          ctx.playground.node.focus();
        }
      }
    });
  });
};

export const preNodeDispose = (ctx: FreeLayoutPluginContext, node: FlowNodeEntity) => {
  const allNodes = ctx.document.getAllNodes();
  const isPreNodes = allNodes.filter((item: FlowNodeEntity) => item.toJSON()?.data?.isPre);
  if (!isPreNodes.length) return;
  isPreNodes.forEach((preNode) => {
    if (preNode.toJSON()?.data?.parentNodeId === node.id) {
      preNode.dispose();
      ctx.history.undoRedoService.getUndoStack().pop();
    }
  });
  // const { data: parentData } = node.toJSON();
  // const { childrenNodeList } = parentData!;
  // if (!childrenNodeList?.length) return;
  // const childrenNodes = childrenNodeList.map((id: string) => ctx.document.getNode(id));
  // const preNode = childrenNodes.find((_node: FlowNodeEntity) => _node.toJSON()?.data?.isPre);
  // if (preNode) {
  //   preNode?.dispose();
  //   ctx.history.undoRedoService.getUndoStack().pop();
  // }
};

export const childNodesEmit = (ctx: FreeLayoutPluginContext, parentNode: FlowNodeEntity) => {
  const { data } = parentNode.toJSON();
  const debouncedHandler = debounce((_ctx, _currentNode) => {
    layoutNodes(_ctx, _currentNode);
  }, 50);
  const updateData = parentNode.form!.updateFormValues;
  const childrenNodeList = data?.childrenNodeList || [];
  childrenNodeList.forEach((id: string) => {
    const node = ctx.document.getNode(id);
    if (!node) return;
    node.onEntityChange(() => {
      debouncedHandler(ctx, parentNode);
    });
    node.onDispose(() => {
      const { childrenNodeList: _childrenNodeList } = parentNode.toJSON()?.data || { childrenNodeList: [] };
      const filteredList = _childrenNodeList?.filter((item: string) => item !== node.id);
      updateData({
        ...data,
        childrenNodeList: filteredList,
      });
      layoutNodes(ctx, parentNode);
    });
  });
};

export const listenHistory = (ctx: FreeLayoutPluginContext): Disposable => {
  const disposable = ctx.history.undoRedoService.onChange((e) => {
    const { type, element } = e as UndoRedoChangeElementEvent;
    if (type === 'undo') {
      // console.log('undo');
      const operationsList = element.getOperations();
      operationsList.forEach((operation) => {
        if (operation.type === 'deleteNode') {
          // operation.value.node.data.isGenerated
          const value = operation?.value as any;
          const isGenerated = value?.node?.data?.isGenerated;
          const parentNodeId = value?.node?.data?.parentNodeId;
          if (isGenerated && parentNodeId) {
            const parentNode = ctx.document.getNode(parentNodeId);
            if (!parentNode) return;
            childNodesEmit(ctx, parentNode);
          }
        }
      });
    }
    // if (e.type === 'redo') {
    //   console.log('redo');
    // }
  });
  return disposable;
};

export const getRelateNodeList = (ctx: FreeLayoutPluginContext, node: FlowNodeEntity) => {
  const data = node.toJSON()?.data || {};
  if (data?.isGenerated) {
    const parentNodeId = data?.parentNodeId;
    const parentNode = ctx.document.getNode(parentNodeId);
    const childrenNodeList = parentNode?.toJSON()?.data?.childrenNodeList || [];
    const childrenNodes: FlowNodeEntity[] = childrenNodeList.map((id: string) => ctx.document.getNode(id)).filter(Boolean);
    return [...childrenNodes];
  }
  return [];
};
export const nodeLevelChange = (ctx: FreeLayoutPluginContext, selectService: WorkflowSelectService) => {
  selectService.onSelectionChanged((nodesList: any) => {
    ctx.document.getAllNodes().forEach((node: FlowNodeEntity) => {
      node.renderData.node.classList.remove('high-level-select');
      node.renderData.node.classList.remove('high-level-select-current');
    });
    const node = nodesList[0] as FlowNodeEntity;
    if (!node) return;
    const data = node.toJSON()?.data || {};
    if (data?.isGenerated) {
      const relateNodeList = getRelateNodeList(ctx, node);
      relateNodeList.forEach((relateNode: FlowNodeEntity) => {
        if (!relateNode.renderData.node.classList.contains('high-level-select')) {
          relateNode.renderData.node.classList.add('high-level-select');
        }
      });
    }
    if (data?.childrenNodeList?.length) {
      const childrenNodeList = data?.childrenNodeList || [];
      const childrenNodes = childrenNodeList.map((id: string) => ctx.document.getNode(id)).filter(Boolean);
      node.renderData.node.classList.add('high-level-select-current');
      childrenNodes.forEach((childrenNode: FlowNodeEntity) => {
        if (!childrenNode.renderData.node.classList.contains('high-level-select')) {
          childrenNode.renderData.node.classList.add('high-level-select');
        }
      });
    }
    zIndexCss(Number(10000));
  });
};

export const lineDrag = (ctx: FreeLayoutPluginContext, dragService: WorkflowDragService) => {
  dragService.onDragLineEventChange((event) => {
    if (event.type === 'onDrag') {
      const nodeList = ctx.document.getAllNodes();
      const editNodeList = nodeList.filter((item: FlowNodeEntity) => {
        return editNodeFlowType.includes(item.flowNodeType as FlowNodeTypeEnum);
      });
      editNodeList.forEach((item: FlowNodeEntity) => {
        if (!item.renderData.node.classList.contains('high-level-select')) {
          item.renderData.node.classList.add('high-level-select');
          zIndexCss(Number(10000));
        }
      });
    } else if (event.type === 'onDragEnd') {
      const nodeList = ctx.document.getAllNodes();
      const editNodeList = nodeList.filter((item: FlowNodeEntity) => {
        return editNodeFlowType.includes(item.flowNodeType as FlowNodeTypeEnum);
      });
      editNodeList.forEach((item: FlowNodeEntity) => {
        if (item.renderData.node.classList.contains('high-level-select')) {
          item.renderData.node.classList.remove('high-level-select');
        }
      });
    }
  });
};

// export const nodeLevelChangeOne =

export const zIndexCss = (zIndex: number) => {
  // 检查是否已存在相同的style标签，如果存在则移除
  const existingStyle = document.getElementById(styleId);
  if (existingStyle) {
    existingStyle.remove();
  }

  // 创建新的style标签
  const style = document.createElement('style');
  style.id = styleId;
  style.type = 'text/css';

  // 定义CSS内容
  const cssContent = `
    .high-level-select {
      z-index: ${zIndex} !important;
    }
    .high-level-select-current {
      z-index: ${zIndex + 1} !important;
    }
  `;

  // 设置CSS内容
  style.innerHTML = cssContent;

  // 插入到head中
  document.head.appendChild(style);
};

// 父节点校准功能，因为有时候会出现编辑节点 childrenNodeList 和真实的子节点对应不上导致报错
export const validateParentNodeChildren = (ctx: FreeLayoutPluginContext) => {
  const allNodes = ctx.document.getAllNodes();
  const editNodeList = allNodes.filter((node: FlowNodeEntity) => {
    return editNodeFlowType.includes(node.flowNodeType as FlowNodeTypeEnum);
  });

  editNodeList.forEach((editNode: FlowNodeEntity) => {
    if (!editNode.form) return;
    const data = editNode.toJSON()?.data;
    const childrenNodeList = data?.childrenNodeList || [];

    if (!childrenNodeList.length) return;

    const validChildrenIds: string[] = [];

    childrenNodeList.forEach((childId: string) => {
      const childNode = ctx.document.getNode(childId);
      if (childNode) {
        validChildrenIds.push(childId);
      }
    });

    if (validChildrenIds.length !== childrenNodeList.length) {
      const updateData = editNode.form.updateFormValues;
      updateData({
        ...data,
        childrenNodeList: validChildrenIds,
      });
    }
  });
};

export const generateNodeDesc = (type: FlowNodeTypeEnum, node: FlowNodeEntity, nodeData: any, passThroughTaskId?: string) => {
  let urlList = [];
  if (type === FlowNodeTypeEnum.IMAGE_NODE) {
    urlList = nodeData?.imageList || [];
  // } else if (type === FlowNodeTypeEnum.AUDIO_NODE) {
  //   urlList = nodeData?.audioList || [];
  } else if (type === FlowNodeTypeEnum.VIDEO_NODE) {
    urlList = nodeData?.videoList || [];
  } else {
    return;
  }
  const [firstItem] = urlList;
  if (!firstItem) return;
  getMediaContentDescription(firstItem.originUrl, node, passThroughTaskId).then((description: string) => {
    const currentData = node.toJSON()?.data;
    if (!currentData) return;
    firstItem.description = description;
    node.form?.updateFormValues({
      ...currentData,
      [type === FlowNodeTypeEnum.IMAGE_NODE ? 'imageList' : 'videoList']: [firstItem],
    });
  }).catch((error) => {
    console.error('获取描述失败', error instanceof Error ? error : JSON.stringify(error));
  });
};
